import React from "react";
import PageContents from "../components/layouts/PageContents";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    CircularProgress,
    IconButton
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ResponsiveTableCell from "../components/ResponsiveTableCell";
import { useSnackbar } from "notistack";
import { INotification } from "../components/notifications/Notifications";
import { useDataUnlessForbidden, apiGet, apiDelete } from "../services/api";
import Moment from "react-moment";
import TableStyle from "../components/shared/Styling/TableStyling";
import Loading from "../components/shared/Loading";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useHistory } from "react-router-dom";

const NotificationsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const tableClasses = TableStyle();

    const [notifications, loadingNotifications] = useDataUnlessForbidden<
        INotification[]
    >(`users/me/notifications/all`, () =>
        enqueueSnackbar(
            "We couldn't retrieve notifications. Please log out and try again.",
            { variant: "error" }
        )
    );

    const [currentNotifications, setCurrentNotifications] = React.useState<
        INotification[]
    >([]);

    React.useEffect(() => {
        if (!loadingNotifications) {
            if (notifications) {
                setCurrentNotifications(notifications);
            }
        }
    }, [notifications, loadingNotifications]);

    const deleteNotification = async (notification: INotification) => {
        const array = [...currentNotifications]; // make a separate copy of the array
        var index = array.indexOf(notification);
        if (index !== -1) {
            array.splice(index, 1);

            setCurrentNotifications(array);
        }

        await apiDelete(`users/me/notifications/${notification.id}`);

        enqueueSnackbar("Notification deleted.", { variant: "success" });
    };

    let history = useHistory();

    const viewNotification = async (notification: INotification) => {
        if (notification.actions) {
            const url = notification.actions["view"] as string;

            history.push(url.replace(/^(?:\/\/|[^/]+)*\//, "/"));
            apiGet(`users/me/notifications/${notification.id}`);
        }
    };

    return (
        <PageContents
            title="Notifications"
            subtitle=""
            isForbidden={false}
            variant="small"
            maxWidth="lg"
        >
            <Table className={tableClasses.table}>
                <TableHead className={tableClasses.header}>
                    <TableRow>
                        <TableCell className={tableClasses.headerCell}>
                            Title
                        </TableCell>
                        <ResponsiveTableCell
                            hiddenWhen="xs"
                            className={tableClasses.headerCell}
                        >
                            Message
                        </ResponsiveTableCell>
                        <ResponsiveTableCell
                            hiddenWhen="sm"
                            className={tableClasses.headerCell}
                        >
                            Time
                        </ResponsiveTableCell>
                        <TableCell
                            className={tableClasses.headerCell}
                        ></TableCell>
                        <TableCell
                            className={tableClasses.headerCell}
                        ></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {currentNotifications.length === 0 && (
                        <TableRow style={{ cursor: "pointer" }}>
                            <TableCell colSpan={4}>
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        position: "absolute",
                                        left: "-15px",
                                        top: "60px"
                                    }}
                                >
                                    No notifications.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {currentNotifications?.map(n => {
                        return (
                            <TableRow key={n.id}>
                                <TableCell>{n.title}</TableCell>
                                <ResponsiveTableCell
                                    hiddenWhen="xs"
                                    style={{ whiteSpace: "pre" }}
                                >
                                    {n.text}
                                </ResponsiveTableCell>
                                <ResponsiveTableCell
                                    hiddenWhen="sm"
                                    style={{ width: "90px" }}
                                >
                                    <Moment fromNow date={n.timestamp} />
                                </ResponsiveTableCell>
                                <ResponsiveTableCell style={{ width: "50px" }}>
                                    {n.actions && (
                                        <IconButton
                                            onClick={() => viewNotification(n)}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    )}
                                </ResponsiveTableCell>
                                <ResponsiveTableCell style={{ width: "50px" }}>
                                    <IconButton
                                        onClick={() => deleteNotification(n)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ResponsiveTableCell>
                            </TableRow>
                        );
                    })}

                    {loadingNotifications && (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Loading>
                                    <CircularProgress />
                                </Loading>
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        position: "absolute",
                                        left: "-15px",
                                        top: "60px"
                                    }}
                                >
                                    Loading Notifications...
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </PageContents>
    );
};

export default NotificationsPage;
